'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'

interface DragDropContextType {
    isDragActive: boolean
    setIsDragActive: (active: boolean) => void
    onFilesDropped?: (files: File[]) => void
    setOnFilesDropped: (callback: (files: File[]) => void) => void
}

const DragDropContext = createContext<DragDropContextType | undefined>(undefined)

export function useDragDrop() {
    const context = useContext(DragDropContext)
    if (!context) {
        throw new Error('useDragDrop must be used within a DragDropProvider')
    }
    return context
}

export function DragDropProvider({ children }: { children: React.ReactNode }) {
    const [isDragActive, setIsDragActive] = useState(false)
    const [onFilesDropped, setOnFilesDropped] = useState<((files: File[]) => void) | undefined>()
    const dragCounterRef = React.useRef(0)

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounterRef.current++
        if (dragCounterRef.current === 1) {
            setIsDragActive(true)
        }
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounterRef.current--
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0
            setIsDragActive(false)
        }
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        dragCounterRef.current = 0
        setIsDragActive(false)

        const files = Array.from(e.dataTransfer.files)
        if (files.length > 0 && onFilesDropped) {
            onFilesDropped(files)
        }
    }, [onFilesDropped])

    // Add window-level event listeners to handle drag cancellation
    React.useEffect(() => {
        const handleWindowDragEnd = (e: DragEvent) => {
            dragCounterRef.current = 0
            setIsDragActive(false)
        }

        const handleWindowDragLeave = (e: DragEvent) => {
            // If dragging outside the window entirely
            if (!e.relatedTarget) {
                dragCounterRef.current = 0
                setIsDragActive(false)
            }
        }

        // Listen for drag events that indicate the drag operation has ended
        window.addEventListener('dragend', handleWindowDragEnd)
        document.addEventListener('dragleave', handleWindowDragLeave)

        return () => {
            window.removeEventListener('dragend', handleWindowDragEnd)
            document.removeEventListener('dragleave', handleWindowDragLeave)
        }
    }, [])

    // Memoize the setOnFilesDropped function to prevent infinite re-renders
    const setOnFilesDroppedCallback = useCallback((callback: (files: File[]) => void) => {
        setOnFilesDropped(() => callback)
    }, [])

    return (
        <DragDropContext.Provider 
            value={{ 
                isDragActive, 
                setIsDragActive, 
                onFilesDropped,
                setOnFilesDropped: setOnFilesDroppedCallback
            }}
        >
            <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="contents"
            >
                {children}
            </div>
        </DragDropContext.Provider>
    )
} 